import React from 'react';
import type { Achievement } from '../../types/social';

type MedalSize = 'xs' | 'sm' | 'md' | 'lg';

interface Props {
  medal: Achievement;
  locked?: boolean;
  size?: MedalSize | number;
  className?: string;
  onClick?: () => void;
  ariaLabel?: string;
}

const SIZE_PX: Record<MedalSize, number> = {
  xs: 32,
  sm: 44,
  md: 64,
  lg: 96,
};

interface ParsedIcon {
  shape: 'shield' | 'star' | 'circle' | 'gem' | 'flame' | 'banner';
  bg: string;
  stroke: string;
  text: string;
  textColor: string;
}

function parseIcon(icon: string): ParsedIcon {
  const parts = Object.fromEntries(
    icon.split(';').map((p) => {
      const idx = p.indexOf(':');
      return idx === -1 ? [p, ''] : [p.slice(0, idx).trim(), p.slice(idx + 1).trim()];
    })
  );
  const shape = (parts.shape || 'shield') as ParsedIcon['shape'];
  return {
    shape,
    bg: parts.bg || '#1B5E20',
    stroke: parts.stroke || '#0B3F12',
    text: parts.text || '',
    textColor: parts.textColor || '#FFFFFF',
  };
}

function fontSizeForText(text: string): number {
  const len = text.length;
  if (len <= 1) return 22;
  if (len <= 2) return 18;
  if (len <= 3) return 14;
  if (len <= 4) return 12;
  return 10;
}

export function MedalIcon({ medal, locked = false, size = 'md', className, onClick, ariaLabel }: Props) {
  const px = typeof size === 'number' ? size : SIZE_PX[size];
  const parsed = parseIcon(medal.icon);
  const opacity = locked ? 0.35 : 1;
  const filter = locked ? 'saturate(0)' : 'none';
  const fs = fontSizeForText(parsed.text);
  const gradId = React.useId();

  const renderShape = () => {
    switch (parsed.shape) {
      case 'shield':
        return (
          <g>
            <path d="M32 4 L56 12 L56 32 Q56 50 32 60 Q8 50 8 32 L8 12 Z" fill={parsed.bg} stroke={parsed.stroke} strokeWidth="2.5" />
            <path d="M32 4 L56 12 L56 32 Q56 50 32 60 Q8 50 8 32 L8 12 Z" fill={`url(#${gradId})`} opacity="0.4" />
          </g>
        );
      case 'star':
        return (
          <polygon points="32,4 39,24 60,24 43,37 49,57 32,45 15,57 21,37 4,24 25,24" fill={parsed.bg} stroke={parsed.stroke} strokeWidth="2.5" />
        );
      case 'circle':
        return (
          <g>
            <circle cx="32" cy="32" r="28" fill={parsed.bg} stroke={parsed.stroke} strokeWidth="2.5" />
            <circle cx="32" cy="32" r="28" fill={`url(#${gradId})`} opacity="0.4" />
          </g>
        );
      case 'gem':
        return (
          <g>
            <polygon points="32,4 56,22 50,58 14,58 8,22" fill={parsed.bg} stroke={parsed.stroke} strokeWidth="2.5" />
            <polygon points="32,4 56,22 50,58 14,58 8,22" fill={`url(#${gradId})`} opacity="0.4" />
          </g>
        );
      case 'flame':
        return (
          <g>
            <path d="M32 4 Q42 16 44 28 Q56 38 50 50 Q44 60 32 60 Q20 60 14 50 Q8 38 20 28 Q22 16 32 4 Z" fill={parsed.bg} stroke={parsed.stroke} strokeWidth="2.5" />
            <path d="M32 4 Q42 16 44 28 Q56 38 50 50 Q44 60 32 60 Q20 60 14 50 Q8 38 20 28 Q22 16 32 4 Z" fill={`url(#${gradId})`} opacity="0.4" />
          </g>
        );
      case 'banner':
        return (
          <g>
            <rect x="8" y="8" width="48" height="44" rx="4" fill={parsed.bg} stroke={parsed.stroke} strokeWidth="2.5" />
            <path d="M8 52 L16 60 L24 52 Z M40 52 L48 60 L56 52 Z" fill={parsed.stroke} />
            <rect x="8" y="8" width="48" height="44" rx="4" fill={`url(#${gradId})`} opacity="0.4" />
          </g>
        );
    }
  };

  const svg = (
    <svg width={px} height={px} viewBox="0 0 64 64" style={{ opacity, filter, transition: 'all 0.2s', display: 'block' }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      {renderShape()}
      {parsed.text && (
        <text
          x="32"
          y={parsed.shape === 'flame' || parsed.shape === 'gem' ? 40 : 38}
          fontSize={fs}
          fontWeight="800"
          fill={parsed.textColor}
          textAnchor="middle"
          style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
        >
          {parsed.text}
        </text>
      )}
    </svg>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel || medal.name}
        className={['p-0 bg-transparent border-0 cursor-pointer rounded-md btn-focus', className || ''].join(' ').trim()}
      >
        {svg}
      </button>
    );
  }

  return (
    <span aria-label={ariaLabel || medal.name} className={['inline-block', className || ''].join(' ').trim()}>
      {svg}
    </span>
  );
}
